/**
 * ScoringConfigEditor.tsx — the governed model-parameters editor (T-368, design N-2/D-2).
 *
 * The whole point of this screen is that a spec'd number cannot be changed casually. It walks
 * the operator through the four steps in order, and each one unlocks the next:
 *
 *   edit → save draft → preview (see the effect) → publish (name the approver) → activate
 *
 * Deliberately NOT a form that "just saves". Publishing requires the approver's name and a
 * reference to their written approval (N-3); activation is a separate click, because that is
 * the step that changes what every user's score is computed from.
 *
 * The LVI weights are absent by design (N-1): they are pipeline-owned and this panel must
 * never be able to move them.
 *
 * Lives outside the Dialog so it is testable in jsdom (Radix portals dialog content) — the
 * same split SettingsDialog/ChangePasswordForm uses.
 */
import { useCallback, useEffect, useState } from 'react'
import {
  activateVersion,
  createDraft,
  fetchActiveConfig,
  previewVersion,
  publishVersion,
  saveDraftValues,
  tierFields,
  tiersToValues,
  type ConfigVersion,
  type PreviewResponse,
  type SiteScoreTiers,
} from '@/lib/adminApi'

type Step = 'editing' | 'saved' | 'previewed' | 'published' | 'activated'

export function ScoringConfigEditor() {
  const [baseline, setBaseline] = useState<SiteScoreTiers | null>(null)
  const [edited, setEdited] = useState<SiteScoreTiers | null>(null)
  const [configVersion, setConfigVersion] = useState<string>('')
  const [note, setNote] = useState('')
  const [approvedBy, setApprovedBy] = useState('')
  const [approvalRef, setApprovalRef] = useState('')
  const [draft, setDraft] = useState<ConfigVersion | null>(null)
  const [preview, setPreview] = useState<PreviewResponse | null>(null)
  const [step, setStep] = useState<Step>('editing')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    fetchActiveConfig('site')
      .then((res) => {
        if (cancelled) return
        // Seed from what is RUNNING (resolved), not from the code defaults, so an operator
        // always edits the live numbers.
        setBaseline(res.resolved)
        setEdited(res.resolved)
        setConfigVersion(res.configVersion)
      })
      .catch((e: unknown) => !cancelled && setError((e as Error).message))
    return () => {
      cancelled = true
    }
  }, [])

  const run = useCallback(async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }, [])

  if (error && !baseline) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {error}
      </p>
    )
  }
  if (!baseline || !edited) {
    return <p className="text-sm text-muted-foreground">Loading model parameters…</p>
  }

  const changed = tiersToValues(edited, baseline)
  const dirty = changed.length > 0

  const setField = (family: keyof SiteScoreTiers, component: string, raw: string) => {
    const value = Number(raw)
    if (!Number.isFinite(value)) return
    setEdited({
      ...edited,
      [family]: { ...(edited[family] as unknown as Record<string, number>), [component]: value },
    } as SiteScoreTiers)
    // Any edit after a save invalidates the review chain — you must re-save and re-preview.
    setStep('editing')
    setPreview(null)
  }

  const onSaveDraft = () =>
    run(async () => {
      const created = await createDraft('site', note)
      await saveDraftValues(created.id, changed)
      setDraft(created)
      setStep('saved')
    })

  const onPreview = () =>
    run(async () => {
      if (!draft) return
      setPreview(await previewVersion(draft.id))
      setStep('previewed')
    })

  const onPublish = () =>
    run(async () => {
      if (!draft) return
      await publishVersion(draft.id, { approvedBy, approvalRef })
      setStep('published')
    })

  const onActivate = () =>
    run(async () => {
      if (!draft) return
      const activated = await activateVersion(draft.id)
      setConfigVersion(`${activated.scope}-v${activated.version}`)
      setStep('activated')
    })

  const canPublish = step === 'previewed' && approvedBy.trim() !== '' && approvalRef.trim() !== ''

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Live configuration: <strong>{configVersion}</strong>. Site-score adjustment values only —
        the LVI weights are set by the scoring pipeline and are not editable here.
      </p>

      <div>
        <label className="text-sm font-medium text-foreground" htmlFor="config-note">
          What is changing, and why
        </label>
        <input
          id="config-note"
          className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground placeholder:text-muted-foreground"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Noreen's Jul-21 revision to the corridor tiers"
        />
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {tierFields(edited).map((f) => (
          <div key={f.label}>
            <label className="text-xs text-muted-foreground" htmlFor={`knob-${f.label}`}>
              {f.label}
            </label>
            <input
              id={`knob-${f.label}`}
              aria-label={f.label}
              type="number"
              step="any"
              className="mt-0.5 w-full rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground placeholder:text-muted-foreground"
              value={f.value}
              onChange={(e) => setField(f.family, f.component, e.target.value)}
            />
          </div>
        ))}
      </div>

      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {/*
        Actions and preview share one row (T-383). The preview used to render after the approval
        fieldset, so by the time you judged "what does this change do" the knob you changed — and
        the button you pressed — had scrolled out of view (T-368 REPORT.md §8(b)). Side by side,
        the effect lands next to the control that produced it. The right column is always
        occupied (a placeholder when there is nothing to show) so running a preview does not
        shove the buttons around. Below `sm` it degrades to stacked, which is still above the
        approval fieldset and so still closer than it was.
      */}
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <div
          role="group"
          aria-label="Configuration actions"
          className="flex flex-wrap gap-2 sm:flex-col sm:flex-nowrap"
        >
          <button
            type="button"
            className="rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
            disabled={!dirty || busy}
            onClick={onSaveDraft}
          >
            Save draft
          </button>
          <button
            type="button"
            className="rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
            disabled={step === 'editing' || busy}
            onClick={onPreview}
          >
            Preview effect
          </button>
          <button
            type="button"
            className="rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
            disabled={!canPublish || busy}
            onClick={onPublish}
          >
            Publish
          </button>
          <button
            type="button"
            className="rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
            disabled={step !== 'published' || busy}
            onClick={onActivate}
          >
            Activate
          </button>
        </div>

        {preview ? (
          <section aria-label="Preview" className="rounded-md border border-border p-3">
            <h4 className="text-sm font-medium text-foreground">Effect on sample sites</h4>
            <ul className="mt-1 space-y-1 text-sm text-foreground">
              {preview.samples.map((s) => (
                <li key={s.name}>
                  <span>{s.name}: </span>
                  <span>
                    {s.before.siteLvi} → {s.after.siteLvi}
                  </span>{' '}
                  <span
                    data-testid={`delta-${s.name}`}
                    className={
                      s.deltaLvi > 0
                        ? 'text-adj-positive'
                        : s.deltaLvi < 0
                          ? 'text-adj-negative'
                          : 'text-adj-zero'
                    }
                  >
                    ({s.deltaLvi > 0 ? '+' : ''}
                    {s.deltaLvi})
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : (
          <p
            data-testid="preview-placeholder"
            className="rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground"
          >
            Save a draft, then “Preview effect” — the change to each sample site appears here,
            beside the buttons.
          </p>
        )}
      </div>

      <fieldset className="rounded-md border border-border p-3">
        <legend className="px-1 text-xs text-muted-foreground">
          Approval (required before publishing)
        </legend>
        <div className="grid gap-2 sm:grid-cols-2">
          <div>
            <label className="text-xs text-muted-foreground" htmlFor="approved-by">
              Approved by
            </label>
            <input
              id="approved-by"
              className="mt-0.5 w-full rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground placeholder:text-muted-foreground"
              value={approvedBy}
              onChange={(e) => setApprovedBy(e.target.value)}
              placeholder="Name of the person who signed this off"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground" htmlFor="approval-ref">
              Approval reference
            </label>
            <input
              id="approval-ref"
              className="mt-0.5 w-full rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground placeholder:text-muted-foreground"
              value={approvalRef}
              onChange={(e) => setApprovalRef(e.target.value)}
              placeholder="Link or reference to the written approval"
            />
          </div>
        </div>
      </fieldset>

      {step === 'activated' ? (
        <p role="status" className="text-sm text-foreground">
          Activated — new scores now use {configVersion}.
        </p>
      ) : null}
    </div>
  )
}
